import ICompanyService from "@/services/ICompanyService";
import CompanyListFilter from "@/models/company/CompanyListFilter";
import AsiListTableOptions from "@/components/common/AsiListTableOptions";
import PaginatedResponse from "@/models/PaginatedResponse";
import CompanyListEntry from "@/models/company/CompanyListEntry";
import Vue from "vue";
import CompanyCreate from "@/models/company/CompanyCreate";
import CreateResponse from "@/models/CreateResponse";
import Company from "@/models/company/Company";
import AvatarCreate from "@/models/attachment/AvatarCreate";
import UserListFilter from "@/models/user/UserListFilter";
import UserListEntry from "@/models/user/UserListEntry";
import OrderListFilter from "@/models/order/OrderListFilter";
import OrderListEntry from "@/models/order/OrderListEntry";
import CompanyUpdate from "@/models/company/CompanyUpdate";
import {CompanyState} from "@/helpers/constants";
import AddressUpdate from "@/models/address/AddressUpdate";
import AddressBillingUpdate from "@/models/address/AddressBillingUpdate";
import BillListFilter from "@/models/bill/BillListFilter";
import BillListEntry from "@/models/bill/BillListEntry";
import ReportListFilter from "@/models/report/ReportListFilter";
import ReportListEntry from "@/models/report/ReportListEntry";
import PlanningEntryListFilter from "@/models/planningEntry/PlanningEntryListFilter";
import PlanningEntryListEntry from "@/models/planningEntry/PlanningEntryListEntry";

export default class AxiosCompanyService implements ICompanyService {

	public company(id: string): Promise<Company> {
		return Vue.$axios.get<Company>(`/company/${id}`).then(res => res.data);
	}

	public companies(filter: CompanyListFilter | null, options: AsiListTableOptions | null): Promise<PaginatedResponse<CompanyListEntry>> {
		return Vue.$axios.get<PaginatedResponse<CompanyListEntry>>('/company', {
			params: {
				...filter,
				...options
			}
		})
			.then(res => res.data);
	}

	public create(model: CompanyCreate): Promise<CreateResponse> {
		return Vue.$axios.post<CreateResponse>('/company', model).then(res => res.data);
	}

	public update(id: string, model: CompanyUpdate): Promise<null> {
		return Vue.$axios.patch(`/company/${id}`, model).then(res => res.data);
	}

	public delete(id: string): Promise<null> {
		return Vue.$axios.delete(`/company/${id}`).then(res => res.data);
	}

	public state(id: string, requestedState: CompanyState): Promise<null> {
		return Vue.$axios.put(`/company/${id}/state`, {
			state: requestedState,
		}).then(res => res.data);
	}

	public updateContactAddress(id: string, model: AddressUpdate): Promise<null> {
		return Vue.$axios.patch(`/company/${id}/contact-address`, model).then(res => res.data);
	}

	public updateBillingAddress(id: string, model: AddressBillingUpdate): Promise<null> {
		return Vue.$axios.patch(`/company/${id}/billing-address`, model).then(res => res.data);
	}

	public uploadAvatar(model: AvatarCreate, onProgress: ((event: ProgressEvent) => void)): Promise<null> {
		const formData = new FormData();
		formData.append('avatar', model.file as Blob, model.file?.name);

		return Vue.$axios.put<null>(`/company/${model.model?.id}/avatar`, formData, {
			onUploadProgress: onProgress,
		}).then(res => res.data);
	}

	public deleteAvatar(model: Company): Promise<null> {
		return Vue.$axios.delete(`/company/${model.id}/avatar`).then(res => res.data);
	}


	public users(id: string, filter: UserListFilter | null, options: AsiListTableOptions | null): Promise<PaginatedResponse<UserListEntry>> {
		return Vue.$axios.get<PaginatedResponse<UserListEntry>>(`/company/${id}/user`, {
			params: {
				...filter,
				...options
			}
		})
			.then(res => res.data);
	}

	public orders(id: string, filter: OrderListFilter | null, options: AsiListTableOptions | null): Promise<PaginatedResponse<OrderListEntry>> {
		return Vue.$axios.get<PaginatedResponse<OrderListEntry>>(`/company/${id}/order`, {
			params: {
				...filter,
				...options
			}
		})
			.then(res => res.data);
	}

	public bills(id: string, filter: BillListFilter | null, options: AsiListTableOptions | null): Promise<PaginatedResponse<BillListEntry>> {
		return Vue.$axios.get<PaginatedResponse<BillListEntry>>(`/company/${id}/bill`, {
			params: {
				...filter,
				...options
			}
		})
			.then(res => res.data);
	}

	public reports(id: string, filter: ReportListFilter | null, options: AsiListTableOptions | null): Promise<PaginatedResponse<ReportListEntry>> {
		return Vue.$axios.get<PaginatedResponse<ReportListEntry>>(`/company/${id}/report`, {
			params: {
				...filter,
				...options
			}
		})
			.then(res => res.data);
	}

	public planningEntries(id: string, filter: PlanningEntryListFilter | null, options: AsiListTableOptions | null): Promise<PaginatedResponse<PlanningEntryListEntry>> {
		return Vue.$axios.get<PaginatedResponse<PlanningEntryListEntry>>(`/company/${id}/planning-entry`, {
			params: {
				...filter,
				...options
			}
		})
			.then(res => res.data);
	}

}
